import React, { Component } from "react";
import { Button, Table, Tag, Spin, message, Input, Select } from "antd";
import { parse } from "qs";
import moment from "moment";
import { fetchNativeTaskInfoByTaskIdApi, fetchS3FileViaProxy } from "../../common/api";
import { decodeLoganFile } from "../../common/logan-decoder";
import "antd/dist/antd.css";

const { Column } = Table;
const { Search } = Input;
const { Option } = Select;

const S3_STATIC_HOST = "https://static.giggleacademy.com/";

const LOG_TYPE_COLORS = {
  1: "blue",
  2: "red",
  3: "orange",
  10: "cyan",
  11: "geekblue",
  12: "volcano",
};

const LOG_TYPE_NAMES = {
  1: "日志类型1",
  2: "日志类型2",
  3: "日志类型3",
  10: "原生端业务日志",
  11: "Flutter端业务日志",
  12: "Unity端业务日志",
};

function getLogTypeName(type) {
  return LOG_TYPE_NAMES[type] || "自定义业务日志";
}

class S3LogDetail extends Component {
  state = {
    loading: true,
    logInfo: null,
    logItems: [],
    filteredItems: [],
    keyword: "",
    selectedLogTypes: [],
    allLogTypes: [],
    error: null,
  };

  componentDidMount() {
    const params = parse(this.props.location.search, { ignoreQueryPrefix: true });
    const { tasks } = params;
    if (tasks) {
      this.loadTaskAndLogs(tasks);
    }
  }

  loadTaskAndLogs = async (taskId) => {
    try {
      const info = await fetchNativeTaskInfoByTaskIdApi(taskId);
      this.setState({ logInfo: info });

      if (!info || !info.s3Key) {
        this.setState({ loading: false, error: "该任务没有S3文件" });
        return;
      }

      const buffer = await fetchS3FileViaProxy(info.s3Key, info.s3BaseUrl);
      const items = decodeLoganFile(buffer);

      const allLogTypes = [...new Set(items.map((item) => parseInt(item.f) || 0))];

      this.setState({
        logItems: items,
        filteredItems: items,
        allLogTypes,
        selectedLogTypes: allLogTypes,
        loading: false,
      });
    } catch (e) {
      console.error(e);
      this.setState({ loading: false, error: e.message });
      message.error("加载日志失败: " + e.message);
    }
  };

  handleFilter = () => {
    const { logItems, keyword, selectedLogTypes } = this.state;
    let filtered = logItems;

    if (selectedLogTypes.length > 0) {
      filtered = filtered.filter((item) => selectedLogTypes.includes(parseInt(item.f) || 0));
    }

    if (keyword) {
      const kw = keyword.toLowerCase();
      filtered = filtered.filter(
        (item) => item.c && item.c.toLowerCase().includes(kw)
      );
    }

    this.setState({ filteredItems: filtered });
  };

  handleS3Download = async () => {
    const { logInfo, logItems } = this.state;
    if (!logInfo || !logInfo.s3Key) return;
    message.loading("正在导出解密日志...", 0);
    try {
      // If already decoded, use cached items; otherwise re-download
      let items = logItems;
      if (!items || items.length === 0) {
        const buffer = await fetchS3FileViaProxy(logInfo.s3Key, logInfo.s3BaseUrl);
        items = decodeLoganFile(buffer);
      }
      const text = items.map(item => {
        const time = item.l ? moment(parseInt(item.l)).format("YYYY-MM-DD HH:mm:ss.SSS") : "";
        return `[${time}] [type:${item.f || ""}] ${item.c || ""}`;
      }).join("\n");
      const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = (logInfo.s3Key.split("/").pop() || "log").replace(/\.[^.]+$/, "") + "_decrypted.log";
      a.click();
      URL.revokeObjectURL(a.href);
      message.destroy();
      message.success(`导出完成，共 ${items.length} 条日志`);
    } catch (e) {
      message.destroy();
      message.error("导出失败: " + e.message);
    }
  };

  render() {
    const { loading, logInfo, filteredItems, error, allLogTypes, selectedLogTypes } = this.state;

    return (
      <div style={{ padding: 20 }}>
        <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 16 }}>
          <Button icon="left" onClick={() => this.props.history.push("/native-list")}>
            返回列表
          </Button>
          {logInfo && (
            <span style={{ fontSize: 14, color: "#666" }}>
              任务#{logInfo.taskId} | {logInfo.appId} | {logInfo.deviceId} |{" "}
              {moment(logInfo.addTime).format("YYYY-MM-DD HH:mm:ss")}
            </span>
          )}
          {logInfo && logInfo.s3Key && (
            <Button icon="download" onClick={this.handleS3Download}>
              下载原始文件
            </Button>
          )}
        </div>

        {error && <div style={{ color: "red", marginBottom: 16 }}>{error}</div>}

        {!loading && !error && (
          <div style={{ marginBottom: 16, display: "flex", gap: 12, alignItems: "center" }}>
            <Search
              placeholder="搜索关键词"
              onSearch={(v) => this.setState({ keyword: v }, this.handleFilter)}
              style={{ width: 300 }}
              allowClear
            />
            <Select
              mode="multiple"
              placeholder="日志类型"
              value={selectedLogTypes}
              onChange={(v) => this.setState({ selectedLogTypes: v }, this.handleFilter)}
              style={{ minWidth: 200 }}
            >
              {allLogTypes.map((t) => (
                <Option key={t} value={t}>
                  {getLogTypeName(t)}
                </Option>
              ))}
            </Select>
            <span style={{ color: "#999" }}>共 {filteredItems.length} 条日志</span>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: 100 }}>
            <Spin size="large" tip="正在下载并解密日志文件..." />
          </div>
        ) : (
          <Table
            size="small"
            dataSource={filteredItems.map((item, idx) => ({ ...item, _key: idx }))}
            rowKey="_key"
            pagination={{ pageSize: 100, showSizeChanger: true, pageSizeOptions: ["50", "100", "200", "500"] }}
            scroll={{ y: "calc(100vh - 280px)" }}
          >
            <Column
              title="时间"
              dataIndex="l"
              key="time"
              width="180px"
              render={(l) => (l ? moment(parseInt(l)).format("YYYY-MM-DD HH:mm:ss.SSS") : "-")}
            />
            <Column
              title="类型"
              dataIndex="f"
              key="type"
              width="140px"
              render={(f) => {
                const t = parseInt(f) || 0;
                return <Tag color={LOG_TYPE_COLORS[t] || "default"}>{getLogTypeName(t)}</Tag>;
              }}
            />
            <Column
              title="内容"
              dataIndex="c"
              key="content"
              render={(c) => (
                <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-all", fontSize: 12, maxHeight: 200, overflow: "auto" }}>
                  {c}
                </pre>
              )}
            />
            <Column
              title="线程"
              dataIndex="n"
              key="thread"
              width="120px"
              render={(n, record) => (
                <span>{n || "-"}{record.m === "1" ? " (主)" : ""}</span>
              )}
            />
          </Table>
        )}
      </div>
    );
  }
}

export default S3LogDetail;
